// Leitura do export do Notion ("Markdown & CSV") sem DOM: monta a árvore de
// páginas, os bancos de dados com propriedades e linhas e as relações. A
// conversão do Markdown para o editor e o envio ficam em importer.js.
//
// Formatos aceitos:
// - novo (2025+): pastas SEM o código da página ("DEV SS/Porfiria Academy/"),
//   ao lado do arquivo "Porfiria Academy <32hex>.md"; títulos repetidos ganham
//   um sufixo curto na pasta ("Paciente Control 4014-857d/");
// - antigo: pastas com o código ("Porfiria Academy <32hex>/").
// Bancos: "Nome <hex>_all.csv" (ou ".csv") + pasta "Nome/" com uma página .md
// por linha, que começa com "# Título" e as propriedades "Nome: valor"
// (valores podem ter várias linhas).

import { unzipSync, strFromU8 } from "fflate";

const HEX = /\s*([0-9a-f]{32})$/i;
const MONTHS_EN = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const MONTHS_PT = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export const COLORS = ["default", "gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"];

// Anexos que nunca vão para o Ordo (chaves e certificados).
export const SENSITIVE_FILE = /(\.(key|pem|ppk|p12|pfx|kdbx|ovpn|jks|keystore)$)|(^id_(rsa|dsa|ecdsa|ed25519))|(ssh[-_]?key)|(private[-_]?key)/i;
export const SENSITIVE_PAGE = /(acesso|credencia|senha|password|token|segredo|secret)/i;
const PERSON_COL = /^(dev|devs|qa|responsáve(l|is)|responsavel|desenvolvedor(a|es)?|pessoa|pessoas|assignee|owner|dono|criado por|created by|autor|solicitante|testador(a)?|analista|person|people|membros?)$/i;
const ID_COL = /^(id|id qa|id dev|código|codigo|nº|n°|ticket)$/i;

// ---------- Zip ----------

export function readZip(bytes, out = new Map()) {
  const files = unzipSync(bytes);
  for (const [name, data] of Object.entries(files)) {
    if (name.endsWith("/")) continue;
    if (/\.zip$/i.test(name)) readZip(data, out);
    else out.set(name, data);
  }
  // Tira a pasta "Export-…" que envolve tudo, se houver.
  const first = new Set([...out.keys()].map((p) => p.split("/")[0]));
  if (first.size === 1) {
    const [root] = first;
    if (/^Export-/i.test(root) && [...out.keys()].every((p) => p.includes("/"))) {
      const strip = new Map();
      for (const [k, v] of out) strip.set(k.slice(root.length + 1), v);
      return strip;
    }
  }
  return out;
}

// ---------- Nomes ----------

const baseName = (path) => path.split("/").pop().replace(/\.(md|csv)$/i, "").replace(/_all$/, "");
export const hexOf = (s) => HEX.exec(String(s).trim())?.[1]?.toLowerCase() || null;
const titleOfName = (s) => String(s).replace(HEX, "").trim();
const dirOf = (path) => path.split("/").slice(0, -1).join("/");
export const normTitle = (s) => String(s || "").normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();

export async function uuidFor(seed) {
  const buf = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(seed)));
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const h = [...buf.slice(0, 16)].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
}

export function resolvePath(base, href) {
  let rel;
  try { rel = decodeURIComponent(href.split(/[?#]/)[0]); } catch { rel = href.split(/[?#]/)[0]; }
  const parts = base.split("/").slice(0, -1);
  for (const seg of rel.split("/")) {
    if (seg === "..") parts.pop();
    else if (seg && seg !== ".") parts.push(seg);
  }
  return parts.join("/");
}

// ---------- CSV ----------

export function parseCsv(text) {
  text = text.replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else field += c;
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// ---------- Valores ----------

export function parseDate(v) {
  if (!v) return null;
  const s = String(v).split("→")[0].trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^([A-Za-z]+)\.? (\d{1,2}), (\d{4})/.exec(s);
  if (m) {
    const mi = MONTHS_EN.findIndex((x) => x.startsWith(m[1].toLowerCase().slice(0, 3)));
    if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  }
  m = /^(\d{1,2}) de ([a-zç]+)\.? de (\d{4})/i.exec(s);
  if (m) {
    const mi = MONTHS_PT.findIndex((x) => x.startsWith(m[2].toLowerCase().slice(0, 3)));
    if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return null;
}

export function parseDateTime(v) {
  const d = parseDate(v);
  if (!d) return null;
  const t = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(String(v).split("→")[0]);
  let h = t ? Number(t[1]) : 12;
  if (t?.[3]?.toUpperCase() === "PM" && h < 12) h += 12;
  if (t?.[3]?.toUpperCase() === "AM" && h === 12) h = 0;
  // Horário de Brasília (o Notion exporta no fuso de quem exportou).
  return Date.parse(`${d}T${String(h).padStart(2, "0")}:${t ? t[2] : "00"}:00-03:00`);
}

export const splitMulti = (v) => String(v).split(/,\s*/).map((s) => s.trim()).filter(Boolean);

// Ordem e cores conhecidas para status (o CSV não traz a ordem do Notion).
const STATUS_ORDER = ["backlog", "a fazer", "to do", "não iniciado", "not started", "aguardando qa", "em andamento", "em desenvolvimento",
  "in progress", "em teste", "em revisão", "revisão", "bloqueada", "bloqueado", "testes qa", "devolver dev", "devolvido pelo qa",
  "devolvido", "aprovada", "aprovado", "concluída", "concluído", "done", "feito", "arquivado", "arquivada"];
export function orderOptions(names) {
  const rank = (n) => {
    const i = STATUS_ORDER.indexOf(n.toLowerCase());
    return i < 0 ? 1000 : i;
  };
  return [...names].sort((a, b) => rank(a) - rank(b));
}
export function optionColor(name, k) {
  const n = name.toLowerCase();
  if (/(conclu|aprovad|done|feito|^ok$|^sim$|^yes$|baixa)/.test(n)) return "green";
  if (/(bloque|devolv|reprov|falh|erro|urgente|crític|critic|não liberar)/.test(n)) return "red";
  if (/(revis)/.test(n)) return "purple";
  if (/(teste|qa|média|media)/.test(n)) return "yellow";
  if (/(andamento|desenvolv|progress|fazendo)/.test(n)) return "blue";
  if (/(alta|high)/.test(n)) return "orange";
  if (/(backlog|arquiv|a fazer|to do|aguard|não inic)/.test(n)) return "gray";
  return COLORS[2 + (k % (COLORS.length - 2))];
}

// Tipo da coluna a partir do nome e dos valores.
export function inferType(name, values) {
  const vals = values.filter((v) => v !== "" && v != null);
  const lname = name.trim().toLowerCase();
  if (PERSON_COL.test(name.trim())) return { type: "person" };
  if (!vals.length) return { type: "text" };
  if (vals.every((v) => /^(yes|no|sim|não|true|false)$/i.test(v))) return { type: "checkbox" };
  if (/^(created time|criado em|created|data de criação)$/i.test(name) && vals.every((v) => parseDate(v))) return { type: "created_time" };
  if (/^(last edited time|atualizado em|última edição|editado em)$/i.test(name) && vals.every((v) => parseDate(v))) return { type: "updated_time" };
  const idm = vals.map((v) => /^([A-Za-z]+)-(\d+)$/.exec(v.trim()));
  if (idm.every(Boolean) && new Set(idm.map((m) => m[1])).size === 1) return { type: "id", prefix: idm[0][1] };
  if (ID_COL.test(name.trim()) && vals.every((v) => /^\d+$/.test(v.trim()))) return { type: "id", prefix: "" };
  if (vals.every((v) => parseDate(v))) return { type: "date" };
  if (vals.every((v) => /^-?\d+([.,]\d+)?%?$/.test(v.trim()))) return { type: "number" };
  if (vals.every((v) => /^https?:\/\/\S+$/i.test(v.trim()))) return { type: "url" };
  if (vals.every((v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()))) return { type: "email" };
  // Multisseleção: listas separadas por vírgula com opções curtas que se repetem.
  const parts = vals.flatMap(splitMulti);
  const distinctParts = new Set(parts);
  const multi = vals.some((v) => /,\s/.test(v)) && !vals.some((v) => /\n/.test(v))
    && parts.every((p) => p.length <= 60) && distinctParts.size <= 40 && distinctParts.size <= Math.max(6, parts.length * 0.6);
  if (multi && !/(descri|observa|detalhe|nota|coment|objetivo|crit[eé]rio|risco|depend|parecer|resumo|motivo|passos|resultado obtido)/i.test(lname)) {
    return { type: "multi_select", options: [...distinctParts] };
  }
  const distinct = new Set(vals.map((v) => v.trim()));
  const long = vals.some((v) => v.length > 40 || /\n/.test(v) || v.split(/\s+/).length > 6);
  if (long || /(descri|observa|detalhe|nota|coment|objetivo|crit[eé]rio|risco|depend|parecer|resumo|motivo|link|url|passos)/i.test(lname)) return { type: "text" };
  if (distinct.size <= 40 && (distinct.size <= vals.length * 0.7 || vals.length <= 5)) return { type: "select", options: [...distinct] };
  return { type: "text" };
}

// Propriedades no topo do .md de uma linha. Um valor continua nas linhas
// seguintes até o próximo "Nome:" conhecido; uma linha em branco encerra o
// bloco, a não ser que a próxima linha com texto seja outra propriedade.
export function parseRowMd(text, known) {
  const lines = text.replace(/^﻿/, "").split(/\r?\n/);
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++;
  let title = null;
  if (/^# /.test(lines[i] || "")) { title = lines[i].slice(2).trim(); i++; }
  while (i < lines.length && !lines[i].trim()) i++;
  const values = {};
  let current = null;
  const propAt = (line) => {
    const m = /^([^:\n]{1,80}):(?: (.*)|$)/.exec(line);
    return m && known.has(m[1].trim()) ? [m[1].trim(), m[2] ?? ""] : null;
  };
  let end = i;
  for (; i < lines.length; i++) {
    const line = lines[i];
    const p = propAt(line);
    if (p) {
      current = p[0];
      values[current] = p[1];
      end = i + 1;
      continue;
    }
    if (!current) break;
    if (!line.trim()) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length && propAt(lines[j])) { values[current] += "\n"; continue; }
      break;
    }
    values[current] += "\n" + line;
    end = i + 1;
  }
  for (const k of Object.keys(values)) values[k] = values[k].replace(/\n+$/, "");
  return { title, values, body: lines.slice(Object.keys(values).length ? end : i).join("\n").replace(/^\s+/, "") };
}

// ---------- Análise do export ----------

// Devolve o plano: nós (páginas, bancos, linhas), grupos de cima (teamspaces),
// pessoas encontradas, anexos e o que foi pulado.
export async function analyze(entries, { userId }) {
  const paths = [...entries.keys()];
  const text = (p) => strFromU8(entries.get(p));
  const nodes = new Map(); // hex -> nó
  const byDir = new Map(); // pasta -> [nós cujo arquivo está nela]
  const addToDir = (n) => {
    if (!byDir.has(n.dir)) byDir.set(n.dir, []);
    byDir.get(n.dir).push(n);
  };

  // Bancos: o _all.csv tem todas as linhas; o .csv sozinho, só a visão padrão.
  for (const p of paths.filter((x) => /\.csv$/i.test(x))) {
    const hex = hexOf(baseName(p));
    if (!hex) continue;
    const isAll = /_all\.csv$/i.test(p);
    const prev = nodes.get(hex);
    if (prev?.csvAll && !isAll) continue;
    const n = prev || { hex, kind: "database", fileTitle: titleOfName(baseName(p)), dir: dirOf(p) };
    Object.assign(n, { csvPath: p, csvAll: isAll });
    if (!prev) { nodes.set(hex, n); addToDir(n); }
  }
  for (const p of paths.filter((x) => /\.md$/i.test(x))) {
    const hex = hexOf(baseName(p));
    if (!hex) continue;
    const prev = nodes.get(hex);
    if (prev?.kind === "database") { prev.mdPath = p; continue; }
    const n = { hex, kind: "page", fileTitle: titleOfName(baseName(p)), dir: dirOf(p), mdPath: p };
    nodes.set(hex, n);
    addToDir(n);
  }

  // Pai de cada nó: a pasta onde o arquivo está corresponde a um arquivo na
  // pasta de cima (formato novo) ou tem o código no nome (formato antigo).
  const dirOwner = new Map();
  const ownerOf = (dir) => {
    if (!dir) return null;
    if (dirOwner.has(dir)) return dirOwner.get(dir);
    const segs = dir.split("/");
    const name = segs.at(-1);
    const up = segs.slice(0, -1).join("/");
    let found = null;
    const h = hexOf(name);
    if (h && nodes.has(h)) found = h;
    if (!found) {
      const cands = (byDir.get(up) || []).filter((c) =>
        (c.fileTitle && c.fileTitle === name) || `${c.fileTitle} ${c.hex.slice(0, 4)}-${c.hex.slice(-4)}` === name || `${c.fileTitle} ${c.hex}` === name);
      const pick = cands.find((c) => c.kind === "database") || cands[0];
      if (pick) found = pick.hex;
    }
    dirOwner.set(dir, found);
    return found;
  };
  for (const n of nodes.values()) {
    n.parentHex = ownerOf(n.dir);
    n.group = n.dir.split("/")[0] || "";
  }
  // O grupo de cima vale para a árvore inteira (a raiz decide).
  for (const n of nodes.values()) {
    let cur = n;
    let guard = 0;
    while (cur.parentHex && nodes.has(cur.parentHex) && guard++ < 60) cur = nodes.get(cur.parentHex);
    n.group = cur.group;
  }
  for (const n of nodes.values()) if (n.parentHex && nodes.get(n.parentHex)?.kind === "database") n.kind = "row";

  // Títulos completos ("# …") e propriedades das linhas.
  const dbs = [...nodes.values()].filter((n) => n.kind === "database");
  for (const db of dbs) {
    const rows = parseCsv(text(db.csvPath));
    db.header = rows[0] || [];
    db.csvRows = rows.slice(1).filter((r) => r.some((c) => c.trim()));
  }
  for (const n of nodes.values()) {
    if (!n.mdPath || n.kind === "database") continue;
    const raw = text(n.mdPath);
    if (n.kind === "row") {
      const db = nodes.get(n.parentHex);
      const known = new Set(db.header.slice(1));
      const parsed = parseRowMd(raw, known);
      n.title = parsed.title ?? n.fileTitle;
      n.mdValues = parsed.values;
      n.md = parsed.body;
    } else {
      const m = /^\s*# (.*)(\r?\n|$)/.exec(raw);
      n.title = m ? m[1].trim() : n.fileTitle;
      n.md = m ? raw.slice(m[0].length) : raw;
    }
  }

  // Nome do banco: o texto do link para o .csv na página de cima ("Tasks DEV’s")
  // vale mais que o nome do arquivo ("Quadro de Tasks").
  for (const n of nodes.values()) {
    if (!n.md) continue;
    for (const m of n.md.matchAll(/\[([^\]]+)\]\(([^)\s]+\.csv)\)/g)) {
      const h = hexOf(baseName(resolvePath(n.mdPath, m[2])));
      const db = h && nodes.get(h);
      if (db?.kind === "database" && !db.linkTitle) db.linkTitle = m[1].trim();
    }
  }
  for (const db of dbs) {
    let mdTitle = null;
    if (db.mdPath && !db.md) {
      const raw = text(db.mdPath);
      const m = /^\s*# (.*)(\r?\n|$)/.exec(raw);
      mdTitle = m ? m[1].trim() : null;
      // O .md do banco costuma ter só o título e o link para o próprio CSV.
      db.description = (m ? raw.slice(m[0].length) : raw)
        .replace(/^\s*\[[^\]]*\]\([^)\s]+\.csv\)\s*$/gm, "")
        // Resumo das visões que o Notion põe no .md do banco ("sort:", "filter:"...).
        .replace(/^(sort|filter|group ?by|calendar ?by):[ \t]*\r?\n(?:[^\n]*:[^\n]*(?:\r?\n|$))*/gim, "")
        .trim();
    }
    // O título dentro do arquivo ("# Tasks DEV’s") é o nome que aparece no
    // Notion; o nome do arquivo ("Quadro de Tasks") é o do bloco na página.
    db.title = mdTitle || db.linkTitle || db.fileTitle;
  }

  // Linhas: casa cada .md com a linha do CSV (título + ID); o CSV tem os
  // valores completos, o .md tem o prefixo do ID (DEV-32) e o corpo.
  for (const db of dbs) {
    const mdRows = [...nodes.values()].filter((n) => n.kind === "row" && n.parentHex === db.hex);
    const idCol = db.header.findIndex((h, j) => j > 0 && ID_COL.test(h.trim()));
    const pool = new Map();
    for (const r of mdRows) {
      const k = normTitle(r.title);
      if (!pool.has(k)) pool.set(k, []);
      pool.get(k).push(r);
    }
    db.order = [];
    for (const [i, r] of db.csvRows.entries()) {
      const t = r[0] || "";
      const cands = pool.get(normTitle(t)) || [];
      let row = null;
      if (idCol > 0) row = cands.find((c) => String(c.mdValues?.[db.header[idCol]] || "").replace(/^\D+-/, "") === String(r[idCol] || "").trim());
      row = row || cands[0] || null;
      if (row) cands.splice(cands.indexOf(row), 1);
      else {
        const hex = (await uuidFor(`${db.hex}:${i}:${t}`)).replace(/-/g, "");
        row = { hex, kind: "row", title: t.trim(), parentHex: db.hex, group: db.group, dir: db.dir, md: "", csvOnly: true };
        nodes.set(hex, row);
      }
      row.values = {};
      for (const [j, h] of db.header.entries()) if (j > 0) row.values[h] = r[j] ?? "";
      for (const [k, v] of Object.entries(row.mdValues || {})) if (!row.values[k]) row.values[k] = v;
      row.legacyIndex = i;
      row.legacyTitle = t;
      if (!row.title) row.title = t.trim();
      db.order.push(row);
    }
    // Linhas que só têm .md (não estavam no CSV exportado).
    for (const list of pool.values()) for (const r of list) {
      r.values = { ...(r.mdValues || {}) };
      db.order.push(r);
    }
  }

  // Tipos das colunas.
  const people = new Map(); // nome -> {name, count, columns:Set}
  for (const db of dbs) {
    db.props = [];
    for (const [j, col] of db.header.entries()) {
      if (j === 0) continue;
      const values = db.order.map((r) => r.values?.[col] ?? "");
      const mdValues = db.order.map((r) => r.mdValues?.[col] ?? "");
      let t = inferType(col, values);
      // ID: o prefixo (DEV-, QA-) só aparece no .md.
      if (t.type === "id" || (t.type === "number" && ID_COL.test(col.trim()))) {
        const pref = mdValues.map((v) => /^([A-Za-z]+)-\d+$/.exec(String(v).trim())?.[1]).find(Boolean);
        t = { type: "id", prefix: pref || t.prefix || "" };
      }
      const pid = "n" + (await uuidFor(`${db.hex}:${col}`)).slice(0, 7);
      const prop = { id: pid, name: col, type: t.type };
      if (t.options) prop.options = orderOptions(t.options.map((o) => o.trim())).map((name, k) => ({ name, color: optionColor(name, k) }));
      if (t.type === "id") prop.prefix = t.prefix;
      if (t.type === "person") {
        for (const v of values) for (const name of splitMulti(v)) {
          const p = people.get(name) || { name, count: 0, columns: new Set() };
          p.count++;
          p.columns.add(col);
          people.set(name, p);
        }
      }
      db.props.push(prop);
    }
  }

  // Relações: o export não traz. Bancos em que muitas linhas repetem o título
  // das linhas de outro (as tasks QA nascem das tasks DEV) ganham uma relação
  // de mão dupla entre eles.
  const usable = (t) => t && !/^(untitled|sem título|sem titulo|nova página)$/i.test(t);
  db: for (const a of dbs) {
    const titlesA = new Map();
    for (const r of a.order) if (usable(normTitle(r.title))) {
      const k = normTitle(r.title);
      if (!titlesA.has(k)) titlesA.set(k, []);
      titlesA.get(k).push(r);
    }
    for (const b of dbs) {
      if (a === b || b.relationTo?.has(a.hex) || !titlesA.size) continue;
      const hits = b.order.filter((r) => titlesA.has(normTitle(r.title)));
      if (hits.length < 3 || hits.length < b.order.length * 0.3) continue;
      const pa = { id: "r" + (await uuidFor(`${a.hex}>${b.hex}`)).slice(0, 7), name: b.title, type: "relation" };
      const pb = { id: "r" + (await uuidFor(`${b.hex}>${a.hex}`)).slice(0, 7), name: a.title, type: "relation" };
      pa.back = pb.id; pb.back = pa.id; pa.twoWay = pb.twoWay = true;
      pa._target = b.hex; pb._target = a.hex;
      a.props.push(pa); b.props.push(pb);
      a.relationTo = a.relationTo || new Set(); a.relationTo.add(b.hex);
      b.relationTo = b.relationTo || new Set(); b.relationTo.add(a.hex);
      for (const r of hits) {
        const targets = titlesA.get(normTitle(r.title));
        r.links = r.links || {};
        r.links[pb.id] = targets.map((t) => t.hex);
        for (const t of targets) {
          t.links = t.links || {};
          (t.links[pa.id] = t.links[pa.id] || []).push(r.hex);
        }
      }
      continue db;
    }
  }

  // Anexos citados no Markdown.
  const assets = new Map(); // caminho -> {path, name, size, sensitive}
  for (const n of nodes.values()) {
    if (!n.md) continue;
    for (const m of n.md.matchAll(/!?\[([^\]]*)\]\(([^)\s]+)\)/g)) {
      const href = m[2];
      if (/^[a-z]+:/i.test(href) || /\.(md|csv)$/i.test(href.split(/[?#]/)[0])) continue;
      const full = resolvePath(n.mdPath, href);
      if (!entries.has(full) || assets.has(full)) continue;
      const name = full.split("/").pop();
      assets.set(full, { path: full, name, size: entries.get(full).length, sensitive: SENSITIVE_FILE.test(name) });
    }
  }

  // Ids no Ordo: derivados do código do Notion (importar de novo atualiza).
  for (const n of nodes.values()) n.id = await uuidFor(`${userId}:notion:${n.hex}`);
  // Linhas que a versão anterior do importador criou só pelo CSV (ids
  // derivados do título): a nova importação as substitui.
  const legacyIds = [];
  for (const db of dbs) {
    for (const r of db.order) {
      if (r.legacyIndex === undefined || r.csvOnly) continue;
      const oldHex = (await uuidFor(`${userId}:notion:${db.hex}:${r.legacyIndex}:${r.legacyTitle}`)).replace(/-/g, "");
      legacyIds.push(await uuidFor(`${userId}:notion:${oldHex}`));
    }
  }

  const groups = new Map();
  for (const n of nodes.values()) {
    if (n.parentHex) continue;
    const g = groups.get(n.group) || { name: n.group, roots: 0, total: 0 };
    g.roots++;
    groups.set(n.group, g);
  }
  for (const n of nodes.values()) if (groups.has(n.group)) groups.get(n.group).total++;

  return {
    nodes, dbs, assets, legacyIds,
    groups: [...groups.values()],
    people: [...people.values()].map((p) => ({ ...p, columns: [...p.columns] })).sort((a, b) => b.count - a.count),
    sensitivePages: [...nodes.values()].filter((n) => n.kind === "page" && SENSITIVE_PAGE.test(n.title || n.fileTitle)).map((n) => ({ hex: n.hex, title: n.title || n.fileTitle })),
    counts: {
      pages: [...nodes.values()].filter((n) => n.kind === "page").length,
      databases: dbs.length,
      rows: [...nodes.values()].filter((n) => n.kind === "row").length,
      withBody: [...nodes.values()].filter((n) => n.kind === "row" && n.md && n.md.trim()).length,
      files: [...assets.values()].filter((a) => !a.sensitive).length,
      skippedFiles: [...assets.values()].filter((a) => a.sensitive).map((a) => a.name),
    },
  };
}

// Pessoa do Notion -> usuário do Ordo (nome igual, primeiro nome ou e-mail).
export function guessUser(name, users) {
  const n = normTitle(name).replace(/[^\p{L}\p{N} ]/gu, "").trim();
  const first = n.split(" ")[0];
  return users.find((u) => normTitle(u.name) === n)
    || users.find((u) => u.email && normTitle(u.email.split("@")[0]).replace(/[^\p{L}\p{N}]/gu, "") === n.replace(/ /g, ""))
    || (first.length >= 3 ? users.find((u) => normTitle(u.name).split(" ")[0] === first) : null)
    || null;
}

// Valor da coluna no formato do Ordo.
export function convertValue(prop, raw, { nodes, peopleMap }) {
  if (raw === undefined || raw === null || raw === "") return undefined;
  switch (prop.type) {
    case "checkbox": return /^(yes|sim|true)$/i.test(raw);
    case "number": return Number(String(raw).replace("%", "").replace(",", "."));
    case "date": return parseDate(raw);
    case "select": return String(raw).trim();
    case "multi_select": return splitMulti(raw);
    case "person": return splitMulti(raw).map((n) => peopleMap.get(n)).filter(Boolean);
    case "id": case "created_time": case "updated_time": return undefined;
    default: return raw;
  }
}

export { strFromU8 };
